import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { FadeIn } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { aud } from "@/lib/api";

const DEFAULTS = {
  sites: 2,
  price: 65,
  daysPerMonth: 8,
  seasonality: 70,
  platformFee: 10,
  setup: 1500,
  insurance: 800,
  cleaning: 600,
  other: 400,
};

const FIELDS = [
  { key: "sites", label: "Number of sites / bookable units", min: 1, max: 20, step: 1 },
  { key: "price", label: "Price per night / booking ($)", min: 10, max: 500, step: 5 },
  { key: "daysPerMonth", label: "Booked nights per site, per month", min: 0, max: 31, step: 1 },
  { key: "seasonality", label: "Seasonality — share of the year you operate (%)", min: 10, max: 100, step: 5 },
  { key: "platformFee", label: "Platform fee (%)", min: 0, max: 25, step: 1 },
  { key: "setup", label: "One-off setup costs ($)", min: 0, max: 100000, step: 500 },
  { key: "insurance", label: "Insurance, per year ($)", min: 0, max: 20000, step: 100 },
  { key: "cleaning", label: "Cleaning, sanitation & maintenance, per year ($)", min: 0, max: 20000, step: 100 },
  { key: "other", label: "Other operating costs, per year ($)", min: 0, max: 20000, step: 100 },
];

const RuralEstimator = () => {
  const [v, setV] = useState(DEFAULTS);

  const r = useMemo(() => {
    const gross = v.sites * v.price * v.daysPerMonth * 12 * (v.seasonality / 100);
    const platform = gross * (v.platformFee / 100);
    const operating = platform + v.insurance + v.cleaning + v.other;
    const net = gross - operating;
    return { gross, platform, operating, net, netAfterSetup: net - v.setup };
  }, [v]);

  return (
    <div data-testid="rural-estimator-page">
      <Seo title="Rural Income Estimator" description="Estimate indicative gross revenue, expenses and net operating income from hosting on your NSW rural land — illustrative only, never guaranteed." />
      <section className="bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/rural" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> Rural Land
          </Link>
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-teal tracking-tight">Rural Income Estimator</h1>
          <p className="mt-3 text-charcoal/70 leading-relaxed max-w-2xl">
            Slide the assumptions to see what hosting could mean for your property. Every figure is
            illustrative — not a promise of earnings — and excludes tax.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
          <FadeIn>
            <div className="bg-white border border-cloud p-7 space-y-6" data-testid="estimator-inputs">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <div className="flex justify-between items-baseline">
                    <label className="text-sm text-charcoal/80">{f.label}</label>
                    <span className="font-mono text-sm text-teal" data-testid={`estimator-value-${f.key}`}>
                      {f.key === "price" || ["setup", "insurance", "cleaning", "other"].includes(f.key)
                        ? aud(v[f.key])
                        : v[f.key]}
                      {["seasonality", "platformFee"].includes(f.key) ? "%" : ""}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={v[f.key]}
                    onChange={(e) => setV((s) => ({ ...s, [f.key]: Number(e.target.value) }))}
                    data-testid={`estimator-slider-${f.key}`}
                    className="mt-2 w-full accent-[#173E47]"
                  />
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.08}>
            <div className="bg-teal text-ivory p-7 sticky top-24" data-testid="estimator-results">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-sand">Indicative annual figures</p>
              <dl className="mt-5 space-y-4">
                <div className="flex justify-between items-baseline border-b border-ivory/15 pb-3">
                  <dt className="text-sm text-ivory/75">Gross booking revenue</dt>
                  <dd className="font-serif text-2xl" data-testid="estimator-gross">{aud(r.gross)}</dd>
                </div>
                <div className="flex justify-between items-baseline border-b border-ivory/15 pb-3">
                  <dt className="text-sm text-ivory/75">Platform fees ({v.platformFee}%)</dt>
                  <dd className="font-mono" data-testid="estimator-platform">−{aud(r.platform)}</dd>
                </div>
                <div className="flex justify-between items-baseline border-b border-ivory/15 pb-3">
                  <dt className="text-sm text-ivory/75">Total operating expenses</dt>
                  <dd className="font-mono" data-testid="estimator-operating">−{aud(r.operating)}</dd>
                </div>
                <div className="flex justify-between items-baseline border-b border-ivory/15 pb-3">
                  <dt className="text-sm text-ivory/75">Net operating income (before tax)</dt>
                  <dd className="font-serif text-3xl text-sand" data-testid="estimator-net">{aud(r.net)}</dd>
                </div>
                <div className="flex justify-between items-baseline">
                  <dt className="text-sm text-ivory/75">Year one, after setup costs</dt>
                  <dd className="font-mono" data-testid="estimator-year-one">{aud(r.netAfterSetup)}</dd>
                </div>
              </dl>
              <p className="mt-6 flex gap-2.5 text-xs text-ivory/70 leading-relaxed">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-sand" />
                Illustrative only — occupancy, demand and costs vary enormously by location and
                season. Hosting income can affect primary production land tax exemptions and is
                generally assessable for tax. Talk to your accountant before committing.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default RuralEstimator;
